import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Flex,
  Select,
  Text,
  Link,
  VStack,
} from "@chakra-ui/react";
import { Alert, AlertTitle, IconButton, styled } from "@mui/material";
import { MdCalendarMonth } from "react-icons/md";
import { ImUsers } from "react-icons/im";
import { FaCircle } from "react-icons/fa";
import { FaImage } from "react-icons/fa6";
import { MdAccessTimeFilled } from "react-icons/md";
import DashboardService from "../services/api";
import { FaLocationDot } from "react-icons/fa6";
import { DateTime } from "luxon";
import InfoIcon from "@mui/icons-material/Info";
import { MonthlyBarChart } from "../components/Chart/MonthlyBarChart";
import YogaPieChartGraph from "../components/Graph/YogaPieChartGraph";
export const YogaSession = () => {
  const localData =
    localStorage.getItem("userdata") || JSON.stringify({ accessCode: "" });
  const access_code = JSON.parse(localData).accessCode;
  const [data, setData] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [month, setMonth] = useState<any>(
    DateTime.now().setZone("Asia/Kolkata").toISO()
  );
  const [locationData, setLocationData] = useState<any>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [totalAttendance, setTotalAttendance] = useState(0);
  const [informationText, setInformationText] = useState("");
  const [pieChartData, setPieChartData] = useState({
    COMPLETED: 0,
    CANCELLED: 0,
    RESCHEDULED: 0,
  });
  const formatDate = (dateString: string) => {
    const date = DateTime.fromFormat(dateString, "MM/dd/yyyy");
    return date.toFormat("dd-LLL-yy (ccc)");
  };
  const getStatusIcon = (status: any) => {
    let icon = <FaCircle />;
    let color = "gray.500";

    if (status === "COMPLETED") {
      color = "green";
    } else if (status === "CANCELLED") {
      color = "red";
    } else if (status === "RESCHEDULED") {
      icon = <MdAccessTimeFilled />;
      color = "#418ed6";
    } else if (status === "HOLIDAY") {
      color = "teal";
    } else if (status === "CANCELLED_BY_SPOC") {
      color = "orange";
    } else if (status === "CANCELLED_BY_EXPERT") {
      color = "purple";
    }

    return { icon, color };
  };
  const processSessions = (sessions: any[]) => {
    const sessionCounts = sessions.reduce((acc: any, session: any) => {
      acc[session.status] = (acc[session.status] || 0) + 1;
      return acc;
    }, {});

    const result = sessions.reduce(
      (acc: any, session: any) => {
        acc.totalAttendees += parseInt(session.no_of_attendee, 10);
        if (session.status === "COMPLETED") {
          acc.completedCount++;
        }
        return acc;
      },
      { totalAttendees: 0, completedCount: 0 }
    );

    return { sessionCounts, result };
  };

  const extractSessionsFromData = (data: any[]) => {
    return data
      .reduce((acc: any, location: any) => {
        const programsWithLocation = location.data.programs.map(
          (program: any) => ({
            ...program,
            location: location.data.city,
          })
        );
        return acc.concat(programsWithLocation);
      }, [])
      .sort((a: any, b: any) => {
        const dateA = DateTime.fromFormat(a.date, "MM/dd/yyyy");
        const dateB = DateTime.fromFormat(b.date, "MM/dd/yyyy");
        return dateA.toMillis() - dateB.toMillis();
      });
  };

  function getMonthsFromAugust2024() {
    const start = DateTime.fromObject({ year: 2024, month: 8 }).setZone(
      "Asia/Kolkata"
    );
    const end = DateTime.now().setZone("Asia/Kolkata");

    const months = [];

    let current = start;

    while (current <= end) {
      months.push({
        label: current.toFormat("MMMM yyyy"),
        value: current.toISO(),
      });
      current = current.plus({ months: 1 });
    }

    return months.reverse();
  }

  function filterByMonth(dataArray: any[]) {
   
    if (!month) return dataArray;
    const referenceDate = DateTime.fromISO(month).setZone("Asia/Kolkata");

    const filteredData = dataArray.filter((item: any) => {
      const itemDate = DateTime.fromFormat(item.date, "MM/dd/yyyy").setZone(
        "Asia/Kolkata"
      );

      return (
        itemDate.year === referenceDate.year &&
        itemDate.month === referenceDate.month
      );
    });

    return filteredData;
  }

  const getFilteredPrograms = (data: any[]) => {
    let sessions = [];

    if (selectedLocation === "") {
      sessions = extractSessionsFromData(data);
      const updatedFilteredData = data.map((item) => {
        return {
          ...item,
          data: {
            ...item.data,
            programs: filterByMonth(item.data.programs),
          },
        };
      });
      setFilteredData(updatedFilteredData);
    } else {
      const locationData = data.filter(
        (location: any) => location.data.city === selectedLocation
      );
      const updatedFilteredData = locationData.map((item) => {
        return {
          ...item,
          data: {
            ...item.data,
            programs: filterByMonth(item.data.programs),
          },
        };
      });
      setFilteredData(updatedFilteredData);
      sessions = extractSessionsFromData(locationData);
    }

    const { sessionCounts, result } = processSessions(filterByMonth(sessions));

    setSessionsCompleted(result.completedCount);
    setTotalAttendance(result.totalAttendees);
    setPieChartData(sessionCounts);

    return filterByMonth(sessions);
  };
  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const convertData = (data: any[]) => {
    const allCities = data.map((item) => capitalize(item.data.city));
    const resultMap: any = {};
    let startDate: any, endDate: any;

    if (month === "") {
      startDate = DateTime.now().minus({ days: 30 });
      endDate = DateTime.now();
    } else {
      const parsedMonth = DateTime.fromISO(month);
      startDate = parsedMonth.startOf("month");
      endDate = parsedMonth.endOf("month");
    }

    const numberOfDays = endDate.diff(startDate, "days").days;

    for (let i = 0; i <= numberOfDays; i++) {
      const currentDate = startDate.plus({ days: i });
      const formattedDate = currentDate.toFormat("dd-LLL (ccc)");

      resultMap[formattedDate] = {
        date: formattedDate,
        day: currentDate.toFormat("ccc"),
      };

      allCities.forEach((city) => {
        resultMap[formattedDate][city] = 0;
      });
    }

    data.forEach((item) => {
      const city = capitalize(item.data.city);
      item.data.programs.forEach((program: any) => {
        const programDate = DateTime.fromFormat(program.date, "MM/dd/yyyy");

        if (
          program.status === "COMPLETED" &&
          programDate >= startDate &&
          programDate <= endDate
        ) {
          const formattedDate = programDate.toFormat("dd-LLL (ccc)");
          const { no_of_attendee } = program;

          if (!resultMap[formattedDate]) {
            resultMap[formattedDate] = {
              date: formattedDate,
              day: programDate.toFormat("ccc"),
            };
            allCities.forEach((city) => {
              resultMap[formattedDate][city] = 0;
            });
          }

          resultMap[formattedDate][city] +=
            selectedLocation === "" ? 1 : parseInt(no_of_attendee, 10);
        }
      });
    });

    const result = Object.values(resultMap);
    return result;
  };
  const convertISOtoString = (date: string) => {
    const parsedMonth = DateTime.fromISO(date);
    return parsedMonth.toFormat("MMMM yyyy");
  };
  const handleChange = (e: any) => {
    setSelectedLocation(e.target.value);
  };
  const fetchData = async () => {
    DashboardService.getYogaData(access_code)
      .then((res: any) => {
        const data = res.data.data;
        const text = res.data.information_text;
        setData(data);
        setLocationData(getFilteredPrograms(data));
        setInformationText(text);
      })
      .catch((e) => {
        console.log(e);
      });
  };
  const CustomAlert = styled(Alert)(({ theme }) => ({
    width: "95%",
    marginBottom: theme.spacing(2),
    backgroundColor: "#c6f6d5",
    // #c05621
    "& .MuiAlert-icon": {
      color: "#2f855a",
    },
  }));
 
  useEffect(() => {
    setLocationData(getFilteredPrograms(data));
  }, [selectedLocation, month]);
  useEffect(() => {
    const start = DateTime.now().setZone("Asia/Kolkata");
    setMonth(start.toISO());
    fetchData();
  }, []);

  return (
    <div>
      <h1 style={{ padding: "25px" }}>Yoga Session Attendance</h1>

      <Flex justifyContent="space-between" alignItems="center" padding="15px">
        <select
          className="yoga-session-select"
          onChange={handleChange}
          value={selectedLocation}
        >
          <option value="" style={{ color: "gray" }}>
            All Location
          </option>
          <option value="noida" style={{ color: "gray" }}>
            Noida
          </option>
          <option value="delhi" style={{ color: "gray" }}>
            Delhi
          </option>
          <option value="kolkata" style={{ color: "gray" }}>
            Kolkata
          </option>
          <option value="hyderabad" style={{ color: "gray" }}>
            Hyderabad
          </option>
          <option value="mumbai" style={{ color: "gray" }}>
            Mumbai
          </option>
          <option value="bangalore" style={{ color: "gray" }}>
            Bangalore
          </option>
          <option value="coimbatore" style={{ color: "gray" }}>
            Coimbatore
          </option>
          <option value="ahmedabad" style={{ color: "gray" }}>
            Ahmedabad
          </option>
          <option value="baroda" style={{ color: "gray" }}>
            Baroda
          </option>
          <option value="madurai" style={{ color: "gray" }}>
            Madurai
          </option>
          <option value="vijayawada" style={{ color: "gray" }}>
            Vijayawada
          </option>
          <option value="indore" style={{ color: "gray" }}>
            Indore
          </option>
          <option value="lucknow" style={{ color: "gray" }}>
            Lucknow
          </option>
        </select>

        <Text fontSize="large" textAlign="right" flex="1" mr={"15px"}>
          Sessions Completed - {sessionsCompleted}
        </Text>
      </Flex>
      <Box display="flex" justifyContent="center">
        <CustomAlert severity="info" icon={<InfoIcon />}>
          {informationText}
        </CustomAlert>
      </Box>
      {locationData && (
        <div
          style={{
            padding: "0 15px",
            margin: "0 15px",
            backgroundColor: "#fff",
            borderRadius: "8px",
          }}
        >
          <div
            className="card-container"
            style={{ gridTemplateColumns: "65% 35%" }}
          >
            <div
              style={{
                // flex: 2,
                // borderTop: "1px solid #eeeff2",
                borderRight: "1px solid #eeeff2",
                // margin:"25px 0"
              }}
            >
              <div
                style={{
                  flex: 2,
                  margin: "25px 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ padding: "0 0 30px 20px" }}>
                    {selectedLocation === ""
                      ? `Sessions completed for ${convertISOtoString(month)}`
                      : `${capitalize(
                          selectedLocation
                        )} attendance for ${convertISOtoString(month)}`}
                  </div>
                  <div style={{ padding: "0 20px 30px 0" }}>
                    <select
                      className="yoga-session-select"
                      onChange={(e) => setMonth(e.target.value)}
                      value={month}
                    >
                      {getMonthsFromAugust2024().map((el: any) => (
                        <option value={el.value} style={{ color: "gray" }}>
                          {el.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <MonthlyBarChart
                    data={convertData(filteredData)}
                    isAttendance={!(selectedLocation === "")}
                  />
                </div>
              </div>
            </div>
            <div>
              <div
                style={{
                  flex: 1,
                  // padding: "10px",
                  // borderTop: "1px solid #eeeff2",
                  margin: "25px 0",
                }}
              >
                <div style={{ padding: "0 0 30px 20px" }}>
                  Session status for {convertISOtoString(month)}
                </div>
                <div>
                  <YogaPieChartGraph obj={pieChartData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sessions list */}
      {locationData.length === 0 ? (
        <Flex justifyContent="center" alignItems="center" height="50vh">
          <Text fontSize="xx-large" color="gray.500">
            No attendance data available for this location. Please choose
            another
          </Text>
        </Flex>
      ) : (
        <VStack spacing={4} padding="15px">
          {locationData.map((session: any) => {
            const { icon, color } = getStatusIcon(session.status);

            return (
              <Box
                key={session.id}
                width="90%"
                borderRadius="25px"
                padding="25px"
                backgroundColor="#fff"
                mb={"15px"}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center" width="15%" justifyContent="left">
                    <FaLocationDot />
                    <Text ml={"5px"}>
                      {session.location.charAt(0).toUpperCase() +
                        session.location.slice(1)}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" width="20%" justifyContent="center">
                    <MdCalendarMonth />
                    <Text ml={"5px"}>{formatDate(session.date)}</Text>
                  </Flex>
                  <Text width="20%" textAlign="center">
                    {session.time}
                  </Text>
                  <Flex alignItems="center" width="30%" justifyContent="center">
                    <ImUsers />
                    <Text ml={"5px"}>{session.no_of_attendee} Attendance</Text>
                  </Flex>
                  <Link
                    style={{ textDecoration: "none" }}
                    href={session.image_url ? session.image_url : "#"}
                    isExternal
                    pointerEvents={session.image_url ? "auto" : "none"}
                    color={session.image_url ? "blue.200" : "gray"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="20%"
                  >
                    <FaImage />
                    <Text ml={"5px"}>View Image</Text>
                  </Link>
                  <Flex alignItems="center" width="20%" justifyContent="left">
                    <Box color={color}>{icon}</Box>
                    <Text ml={"5px"}>
                      {session.modified_status
                        ? session.modified_status
                        : session.status}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      )}
    </div>
  );
};
